import React, { useState, useEffect, useMemo, useCallback } from "react";
import "./Products.css";
import "./ProductsBulkStyles.css";
import { getAuth, signInAnonymously } from "firebase/auth";
import config from "../../config";
import UpdateProduct from "./ManageProduct";
import CreateProductForm from "./CreateProductForm";
import OfflineOrderModal from "./OfflineOrderModal.js";

// 🔹 HELPER: Get main image for product cards
const getMainImage = (product) => {
  return (
    product.images?.find(
      (img) => img.image_type === "PRIMARY" || img.imageType === "PRIMARY"
    )?.image_url ||
    product.images?.find(
      (img) => img.image_type === "PRIMARY" || img.imageType === "PRIMARY"
    )?.imageUrl ||
    product.images?.[0]?.image_url ||
    product.images?.[0]?.imageUrl ||
    "https://via.placeholder.com/200x200?text=No+Image"
  );
};

export default function Products() {
  const [products, setProducts] = useState([]);
  const [inactiveProducts, setInactiveProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [subCategoryFilter, setSubCategoryFilter] = useState("");
  const [sellerFilter, setSellerFilter] = useState("");
  const [subCategories, setSubCategories] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showJsonForm, setShowJsonForm] = useState(false);
  const [expandedProducts, setExpandedProducts] = useState(new Set());
  const [successMessage, setSuccessMessage] = useState("");
  const [showManageProduct, setShowManageProduct] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [activeTab, setActiveTab] = useState("active"); // 'active' | 'review'

  // 🔹 NEW: Bulk operations state
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState(new Set());
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [bulkComment, setBulkComment] = useState("");
  const [bulkLoading, setBulkLoading] = useState(false);
  const [offlineOrderMode, setOfflineOrderMode] = useState(false);
const [showOfflineOrderModal, setShowOfflineOrderModal] = useState(false);

  useEffect(() => {
    fetchProducts();
    fetchInactiveProducts();
    fetchSellers();
    // Keep anonymous sign-in for Firebase image usage, etc.
    signInAnonymously(getAuth()).catch(console.error);
  }, []);

  // Fetch subcategories when category changes
  useEffect(() => {
    if (!categoryFilter) {
      setSubCategories([]);
      setSubCategoryFilter("");
      return;
    }

    const fetchSubCategories = async () => {
      try {
        const encoded = encodeURIComponent(categoryFilter);
        const res = await fetch(`${config.baseUrl}/api/subCategories/category/${encoded}`);
        if (res.ok) {
          const data = await res.json();
          setSubCategories(data || []);
        } else {
          setSubCategories([]);
        }
      } catch (err) {
        console.error('Failed to fetch subcategories:', err);
        setSubCategories([]);
      }
    };

    setSubCategoryFilter(""); // Reset subcategory filter when category changes
    fetchSubCategories();
  }, [categoryFilter]);

  const fetchSellers = async () => {
    try {
      const res = await fetch(`${config.baseUrl}/api/admin/sellers/approved`);
      if (res.ok) {
        const data = await res.json();
        setSellers(data || []);
      }
    } catch (err) {
      console.error('Failed to fetch sellers:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await fetch(`${config.baseUrl}/api/products`);
      if (!res.ok) throw new Error("Failed to fetch products");
      const data = await res.json();
      setProducts(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const refreshProducts = async () => {
    try {
      const res = await fetch(`${config.baseUrl}/api/products`);
      if (res.ok) {
        const data = await res.json();
        setProducts(data);
      }
    } catch (err) {
      console.error("Failed to refresh products:", err);
    }
  };

  const fetchInactiveProducts = async () => {
    try {
      const res = await fetch(`${config.baseUrl}/api/products/admin/inactive`);
      if (res.ok) {
        const data = await res.json();
        setInactiveProducts(data);
      }
    } catch (err) {
      console.error("Failed to fetch inactive products:", err);
    }
  };

  // 🔹 NEW: Bulk selection functions
  const toggleBulkMode = () => {
    setBulkMode(!bulkMode);
    setSelectedProductIds(new Set());
  };

  const toggleOfflineOrderMode = () => {
    setOfflineOrderMode(!offlineOrderMode);
    setSelectedProductIds(new Set());
    // Exit bulk mode when entering offline order mode
    if (!offlineOrderMode && bulkMode) {
      setBulkMode(false);
    }
  };

  const toggleSelectProduct = (productId) => {
    const newSelected = new Set(selectedProductIds);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedProductIds(newSelected);
  };

  const selectAllProducts = () => {
    const currentProducts = filteredActiveProducts;
    if (selectedProductIds.size === currentProducts.length) {
      setSelectedProductIds(new Set());
    } else {
      const allIds = new Set(currentProducts.map(p => p.productId));
      setSelectedProductIds(allIds);
    }
  };

  // 🔹 ENHANCED: Optimistic UI helper
  const showSuccessMessage = useCallback((message, duration = 4000) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(""), duration);
  }, []);

  // 🔹 ENHANCED: Optimistic UI inactivate (single product)
  const inactivateProduct = useCallback(async (productId, comment) => {
    const safeComment = comment && comment.trim().length > 0 ? comment.trim() : "Temporarily inactive";

    // 📸 Snapshot for revert
    const productToInactivate = products.find(p => p.productId === productId);
    const originalIndex = products.findIndex(p => p.productId === productId);

    if (!productToInactivate) return;

    const updatedProduct = {
      ...productToInactivate,
      active: false,
      wasActive: true,
      adminComment: safeComment
    };

    try {
      // ⚡ Optimistic UI update
      setProducts(prev => prev.filter(p => p.productId !== productId));
      setInactiveProducts(prev => [...prev, updatedProduct]);
      showSuccessMessage("Product inactivated!");

      // 🌐 Background API call
      const url = `${config.baseUrl}/api/products/${productId}/active?active=false&adminComment=${encodeURIComponent(safeComment)}`;
      const res = await fetch(url, {
        method: "PATCH",
        headers: { Accept: "application/json" },
      });

      if (!res.ok) {
        throw new Error(`Failed to inactivate product (Status: ${res.status})`);
      }
    } catch (err) {
      console.error("Inactivate product error:", err);

      // 🔄 Revert optimistic update
      setProducts(prev => {
        const newProducts = [...prev];
        newProducts.splice(originalIndex, 0, productToInactivate);
        return newProducts;
      });
      setInactiveProducts(prev => prev.filter(p => p.productId !== productId));

      alert("Error inactivating product: " + err.message);
    }
  }, [products, showSuccessMessage]);

  // 🔹 NEW: Enhanced bulk inactivate function with loading state and order preservation
  const bulkInactivate = useCallback(async () => {
    const comment = bulkComment.trim() || "Temporarily inactive";

    // 📸 Snapshot for revert (BEFORE clearing selectedProductIds)
    const selectedIdsSnapshot = new Set(selectedProductIds);
    const productsSnapshot = products.filter(p => selectedIdsSnapshot.has(p.productId));

    // 🔹 Store original indexes for proper revert order
    const originalIndexes = new Map();
    productsSnapshot.forEach(product => {
      const index = products.findIndex(p => p.productId === product.productId);
      originalIndexes.set(product.productId, index);
    });

    if (productsSnapshot.length === 0) return;

    const updatedProducts = productsSnapshot.map(product => ({
      ...product,
      active: false,
      wasActive: true,
      adminComment: comment
    }));

    // 🔄 Start loading
    setBulkLoading(true);

    try {
      // ⚡ Optimistic UI update
      setProducts(prev => prev.filter(p => !selectedIdsSnapshot.has(p.productId)));
      setInactiveProducts(prev => [...prev, ...updatedProducts]);

      // Reset UI state
      setSelectedProductIds(new Set());
      setBulkMode(false);
      setShowBulkConfirm(false);
      setBulkComment("");
      showSuccessMessage(`${productsSnapshot.length} products inactivated!`);

      // 🌐 Background API call
      const res = await fetch(`${config.baseUrl}/api/products/bulk-active`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body: JSON.stringify({
          productIds: Array.from(selectedIdsSnapshot),
          active: false,
          adminComment: comment
        })
      });

      if (!res.ok) {
        throw new Error(`Failed to bulk inactivate products (Status: ${res.status})`);
      }

      // 🎯 UX Enhancement: Switch to review tab after successful bulk operation
      setActiveTab("review");
    } catch (err) {
      console.error("Bulk inactivate error:", err);

      // 🔄 Revert optimistic update using snapshot with preserved order
      setProducts(prev => {
        const newProducts = [...prev];
        // Sort by original index to maintain order
        const sortedProducts = productsSnapshot.sort((a, b) =>
          originalIndexes.get(a.productId) - originalIndexes.get(b.productId)
        );

        // Insert each product at its original position
        sortedProducts.forEach(product => {
          const originalIndex = originalIndexes.get(product.productId);
          // Adjust index for previously inserted items
          const adjustedIndex = Math.min(originalIndex, newProducts.length);
          newProducts.splice(adjustedIndex, 0, product);
        });

        return newProducts;
      });
      setInactiveProducts(prev => prev.filter(p => !selectedIdsSnapshot.has(p.productId)));

      alert("Error inactivating products: " + err.message);
    } finally {
      // 🔄 End loading
      setBulkLoading(false);
    }
  }, [bulkComment, selectedProductIds, products, showSuccessMessage]);

  // 🔹 ENHANCED: Optimistic UI approve
  const approveProduct = useCallback(async (productId, comment) => {
    const safeComment = comment && comment.trim().length > 0 ? comment.trim() : "Approved";

    // 📸 Snapshot for revert
    const productToApprove = inactiveProducts.find(p => p.productId === productId);

    if (!productToApprove) return;

    const updatedProduct = {
      ...productToApprove,
      active: true,
      adminComment: null,
      wasActive: false
    };

    try {
      // ⚡ Optimistic UI update
      setInactiveProducts(prev => prev.filter(p => p.productId !== productId));
      setProducts(prev => [...prev, updatedProduct]);
      showSuccessMessage("Product approved and activated!");

      // 🌐 Background API call
      const url = `${config.baseUrl}/api/products/${productId}/active?active=true&adminComment=${encodeURIComponent(safeComment)}`;
      const res = await fetch(url, {
        method: "PATCH",
        headers: { Accept: "application/json" },
      });

      if (!res.ok) {
        throw new Error(`Failed to approve product (Status: ${res.status})`);
      }
    } catch (err) {
      console.error("Approve product error:", err);

      // 🔄 Revert optimistic update
      setInactiveProducts(prev => [...prev, productToApprove]);
      setProducts(prev => prev.filter(p => p.productId !== productId));

      alert("Error approving product: " + err.message);
    }
  }, [inactiveProducts, showSuccessMessage]);

  // 🔹 ENHANCED: Optimistic UI activate
  const activateProduct = useCallback(async (productId, comment) => {
    const safeComment = comment && comment.trim().length > 0 ? comment.trim() : "Reactivated";

    // 📸 Snapshot for revert
    const productToActivate = inactiveProducts.find(p => p.productId === productId);

    if (!productToActivate) return;

    const updatedProduct = {
      ...productToActivate,
      active: true,
      adminComment: null,
      wasActive: false
    };

    try {
      // ⚡ Optimistic UI update
      setInactiveProducts(prev => prev.filter(p => p.productId !== productId));
      setProducts(prev => [...prev, updatedProduct]);
      showSuccessMessage("Product activated successfully!");

      // 🌐 Background API call
      const url = `${config.baseUrl}/api/products/${productId}/active?active=true&adminComment=${encodeURIComponent(safeComment)}`;
      const res = await fetch(url, {
        method: "PATCH",
        headers: { Accept: "application/json" },
      });

      if (!res.ok) {
        throw new Error(`Failed to activate product (Status: ${res.status})`);
      }
    } catch (err) {
      console.error("Activate product error:", err);

      // 🔄 Revert optimistic update
      setInactiveProducts(prev => [...prev, productToActivate]);
      setProducts(prev => prev.filter(p => p.productId !== productId));

      alert("Error activating product: " + err.message);
    }
  }, [inactiveProducts, showSuccessMessage]);

  // 🔹 ENHANCED: Optimistic UI reject
  const rejectProduct = useCallback(async (productId, comment) => {
    if (!comment || !comment.trim()) {
      alert("Please add a comment when rejecting a product.");
      return;
    }

    // 📸 Snapshot for revert
    const productToReject = inactiveProducts.find(p => p.productId === productId);

    if (!productToReject) return;

    const updatedProduct = {
      ...productToReject,
      active: false,
      adminComment: comment.trim()
    };

    try {
      // ⚡ Optimistic UI update
      setInactiveProducts(prev =>
        prev.map(p => p.productId === productId ? updatedProduct : p)
      );
      showSuccessMessage("Product rejected.");

      // 🌐 Background API call
      const url = `${config.baseUrl}/api/products/${productId}/active?active=false&adminComment=${encodeURIComponent(comment.trim())}`;
      const res = await fetch(url, {
        method: "PATCH",
        headers: { Accept: "application/json" },
      });

      if (!res.ok) {
        throw new Error(`Failed to reject product (Status: ${res.status})`);
      }
    } catch (err) {
      console.error("Reject product error:", err);

      // 🔄 Revert optimistic update
      setInactiveProducts(prev =>
        prev.map(p => p.productId === productId ? productToReject : p)
      );

      alert("Error rejecting product: " + err.message);
    }
  }, [inactiveProducts, showSuccessMessage]);

  const deleteProduct = async (productId) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        const res = await fetch(
          `${config.baseUrl}/api/products/${productId}`,
          {
            method: "DELETE",
          }
        );
        if (!res.ok) throw new Error("Failed to delete product");
        const message = await res.text();
        setProducts((prev) =>
          prev.filter((prod) => prod.productId !== productId)
        );
        setSuccessMessage(message || "Product deleted successfully.");
        setTimeout(() => setSuccessMessage(""), 4000);
      } catch (err) {
        alert("Error deleting product: " + err.message);
      }
    }
  };

  const toggleExpanded = (productId) => {
    const newExpanded = new Set(expandedProducts);
    if (newExpanded.has(productId)) {
      newExpanded.delete(productId);
    } else {
      newExpanded.add(productId);
    }
    setExpandedProducts(newExpanded);
  };

  /* ---------------- FILTERING LOGIC WITH PERFORMANCE OPTIMIZATION ---------------- */

  // Only active products should appear in Active tab
  const activeProducts = products.filter((p) => p.active);

  // 🔹 PERFORMANCE: Memoized filtering to prevent unnecessary re-computation
  const filteredActiveProducts = useMemo(() => {
    return activeProducts.filter((product) => {
      const productName = product.productName || "";
      const productDescription = product.productDescription || "";
      const productId = product.productId ? String(product.productId) : "";

      const search = searchTerm.trim().toLowerCase();

      const matchesSearch =
        !search ||
        productName.toLowerCase().includes(search) ||
        productDescription.toLowerCase().includes(search) ||
        productId.includes(search);

      const matchesCategory =
        !categoryFilter ||
        product.category?.name === categoryFilter ||
        product.categoryName === categoryFilter;

      const matchesSubCategory =
        !subCategoryFilter ||
        product.subCategory?.subCategoryName === subCategoryFilter ||
        product.subCategoryName === subCategoryFilter;

      const matchesSeller =
        !sellerFilter ||
        product.seller?.sellerName === sellerFilter ||
        product.sellerName === sellerFilter ||
        (product.seller?.userId && String(product.seller.userId) === sellerFilter) ||
        (product.sellerId && String(product.sellerId) === sellerFilter);

      return matchesSearch && matchesCategory && matchesSubCategory && matchesSeller;
    });
  }, [activeProducts, searchTerm, categoryFilter, subCategoryFilter, sellerFilter]);

  /* ---------------- INACTIVE = PENDING + REJECTED + TEMPORARILY INACTIVE ---------------- */

  // Products submitted for review (never been active) with no admin comment
  const pendingReviewProducts = inactiveProducts.filter(
    (p) => (!p.adminComment || p.adminComment.trim() === "") && !p.wasActive
  );

  // Products that were reviewed and rejected during approval process
  const rejectedProducts = inactiveProducts.filter(
    (p) => p.adminComment &&
      p.adminComment.trim() !== "" &&
      !p.wasActive &&
      !p.adminComment.includes("Temporarily inactive") &&
      !p.adminComment.includes("Sold offline") &&
      !p.adminComment.includes("Out of stock")
  );

  // Products that were active but then inactivated by admin
  const temporarilyInactiveProducts = inactiveProducts.filter(
    (p) => p.wasActive ||
      (p.adminComment && (
        p.adminComment.includes("Temporarily inactive") ||
        p.adminComment.includes("Sold offline") ||
        p.adminComment.includes("Out of stock")
      ))
  );

  if (loading) return <div className="loading">Loading products...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  return (
    <div className="products-section">
      {/* HEADER */}
      <div className="section-header">
        <div className="header-content">
          <h2>Product Management</h2>
          <p>Manage your complete product catalog with variants and images</p>
        </div>
        <div className="header-actions">
          <button className="btn-json" onClick={() => setShowJsonForm(true)}>
            <span className="btn-icon">📋</span>
            Add JSON Product
          </button>
          <button
            className="btn-primary"
            onClick={() => setShowCreateForm(true)}
          >
            <span className="btn-icon">➕</span>
            Add Product
          </button>
        </div>
      </div>

      {/* TABS */}
      <div className="tabs-bar">
        <button
          className={`tab-button ${activeTab === "active" ? "active" : ""}`}
          onClick={() => setActiveTab("active")}
        >
          <span className="tab-icon">📦</span>
          Active Products ({filteredActiveProducts.length})
        </button>
        <button
          className={`tab-button ${activeTab === "review" ? "active" : ""}`}
          onClick={() => setActiveTab("review")}
        >
          <span className="tab-icon">📋</span>
          Review &amp; Approve ({pendingReviewProducts.length + rejectedProducts.length + temporarilyInactiveProducts.length})
        </button>
      </div>

      {/* -------- ACTIVE TAB -------- */}
      {activeTab === "active" && (
        <>
          <div className="filters-bar">
            <input
              type="text"
              placeholder="Search products by name, description, or product ID..."
              className="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <select
              className="category-filter"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="">All Categories</option>
              <option value="Boys">Boys</option>
              <option value="Girls">Girls</option>
              <option value="Ladies">Ladies</option>
              <option value="Men">Men</option>
              <option value="Women">Women</option>
            </select>
            <select
              className="subcategory-filter"
              value={subCategoryFilter}
              onChange={(e) => setSubCategoryFilter(e.target.value)}
              disabled={!categoryFilter}
            >
              <option value="">All Sub-Categories</option>
              {subCategories.map(subCat => (
                <option key={subCat.subCategoryId} value={subCat.subCategoryName}>
                  {subCat.subCategoryName}
                </option>
              ))}
            </select>
            <select
              className="seller-filter"
              value={sellerFilter}
              onChange={(e) => setSellerFilter(e.target.value)}
            >
              <option value="">All Sellers</option>
              {sellers.map(seller => (
                <option key={seller.sellerId || seller.userId} value={seller.sellerName}>
                  {seller.sellerName}
                </option>
              ))}
            </select>
          </div>

          {/* 🔹 NEW: Bulk Actions Bar */}
          <div className="bulk-actions-bar">
            <div className="bulk-controls">
              <button
                className={`btn-bulk-toggle ${bulkMode ? 'active' : ''}`}
                onClick={toggleBulkMode}
              >
                <span className="btn-icon">{bulkMode ? '✓' : '☐'}</span>
                {bulkMode ? 'Exit Bulk Mode' : 'Bulk Select'}
              </button>

              <button
                className={`btn-offline-order-toggle ${offlineOrderMode ? 'active' : ''}`}
                onClick={toggleOfflineOrderMode}
              >
                <span className="btn-icon">{offlineOrderMode ? '🛒' : '🛍️'}</span>
                {offlineOrderMode ? 'Exit Offline Order Mode' : 'Place Offline Order'}
              </button>

              {(bulkMode || offlineOrderMode) && filteredActiveProducts.length > 0 && (
                <>
                  <button
                    className="btn-select-all"
                    onClick={selectAllProducts}
                  >
                    <span className="btn-icon">☑</span>
                    {selectedProductIds.size === filteredActiveProducts.length ? 'Deselect All' : 'Select All'}
                  </button>

                  {bulkMode && selectedProductIds.size > 0 && (
                    <button
                      className="btn-bulk-inactivate"
                      onClick={() => setShowBulkConfirm(true)}
                    >
                      <span className="btn-icon">📴</span>
                      Inactivate Selected ({selectedProductIds.size})
                    </button>
                  )}

                  {offlineOrderMode && selectedProductIds.size > 0 && (
                    <button
                      className="btn-create-offline-order"
                      onClick={() => setShowOfflineOrderModal(true)}
                    >
                      <span className="btn-icon">🛒</span>
                      Create Offline Order ({selectedProductIds.size})
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="products-grid">
            {filteredActiveProducts.length === 0 ? (
              <div className="no-products">
                <div className="no-products-icon">📦</div>
                <h3>No active products found</h3>
                <p>Start by adding your first product</p>
                <button
                  className="btn-primary"
                  onClick={() => setShowCreateForm(true)}
                >
                  Add Your First Product
                </button>
              </div>
            ) : (
              filteredActiveProducts.map((product) => (
                <ProductCard
                  key={product.productId}
                  product={product}
                  isExpanded={expandedProducts.has(product.productId)}
                  onToggleExpanded={() => toggleExpanded(product.productId)}
                  onDelete={() => deleteProduct(product.productId)}
                  onInactivate={(comment) => inactivateProduct(product.productId, comment)}
                  onManage={() => {
                    setSelectedProduct(product);
                    setShowManageProduct(true);
                  }}
                  // 🔹 NEW: Bulk selection props
                  bulkMode={bulkMode || offlineOrderMode}
                  isSelected={selectedProductIds.has(product.productId)}
                  onToggleSelect={() => toggleSelectProduct(product.productId)}
                />
              ))
            )}
          </div>
        </>
      )}

      {/* -------- REVIEW / REJECT TAB -------- */}
      {activeTab === "review" && (
        <>
          {/* Pending Review */}
          <div className="review-section">
            <h3>Pending Review &amp; Approval</h3>
            {pendingReviewProducts.length === 0 ? (
              <div className="no-products">
                <div className="no-products-icon">✓</div>
                <h3>No pending products</h3>
                <p>All submitted products are either approved or rejected.</p>
              </div>
            ) : (
              <div className="products-grid">
                {pendingReviewProducts.map((product) => (
                  <InactiveProductCard
                    key={product.productId}
                    product={product}
                    onApprove={(comment) =>
                      approveProduct(product.productId, comment)
                    }
                    onReject={(comment) =>
                      rejectProduct(product.productId, comment)
                    }
                    isExpanded={expandedProducts.has(product.productId)}
                    onToggleExpanded={() =>
                      toggleExpanded(product.productId)
                    }
                  />
                ))}
              </div>
            )}
          </div>

          {/* Rejected */}
          <div className="review-section">
            <h3>Rejected Products</h3>
            {rejectedProducts.length === 0 ? (
              <div className="no-products">
                <div className="no-products-icon">🧺</div>
                <h3>No rejected products</h3>
                <p>Rejections will appear here with admin comments.</p>
              </div>
            ) : (
              <div className="products-grid">
                {rejectedProducts.map((product) => (
                  <RejectedProductCard
                    key={product.productId}
                    product={product}
                    onActivate={(comment) => activateProduct(product.productId, comment)}
                    isExpanded={expandedProducts.has(product.productId)}
                    onToggleExpanded={() =>
                      toggleExpanded(product.productId)
                    }
                  />
                ))}
              </div>
            )}
          </div>

          {/* Temporarily Inactive */}
          <div className="review-section">
            <h3>Temporarily Inactive Products</h3>
            <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '16px' }}>Products that were active but temporarily inactivated (can be reactivated anytime)</p>
            {temporarilyInactiveProducts.length === 0 ? (
              <div className="no-products">
                <div className="no-products-icon">📴</div>
                <h3>No temporarily inactive products</h3>
                <p>Inactivated products will appear here.</p>
              </div>
            ) : (
              <div className="products-grid">
                {temporarilyInactiveProducts.map((product) => (
                  <TemporarilyInactiveProductCard
                    key={product.productId}
                    product={product}
                    onActivate={(comment) => activateProduct(product.productId, comment)}
                    isExpanded={expandedProducts.has(product.productId)}
                    onToggleExpanded={() =>
                      toggleExpanded(product.productId)
                    }
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* 🔹 NEW: Bulk Inactivate Confirmation Modal */}
      {showBulkConfirm && (
        <div className="modal-overlay">
          <div className="modal-content bulk-confirm-modal">
            <div className="modal-header">
              <h3>Confirm Bulk Inactivation</h3>
              <button className="close-btn" onClick={() => setShowBulkConfirm(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to inactivate {selectedProductIds.size} selected products?</p>
              <div className="form-group">
                <label>Reason (optional):</label>
                <textarea
                  value={bulkComment}
                  onChange={(e) => setBulkComment(e.target.value)}
                  placeholder="e.g., Sold offline, Out of stock, etc."
                  rows="3"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={() => {
                  setShowBulkConfirm(false);
                  setBulkComment("");
                }}
                disabled={bulkLoading}
              >
                Cancel
              </button>
              <button
                className="btn-confirm-bulk"
                onClick={bulkInactivate}
                disabled={bulkLoading}
              >
                {bulkLoading ? "Inactivating..." : `Inactivate ${selectedProductIds.size} Products`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODALS */}
      {showCreateForm && (
        <CreateProductForm
          onClose={() => setShowCreateForm(false)}
          onSuccess={() => {
            setShowCreateForm(false);
            showSuccessMessage("Product created successfully! It may take a moment to appear.");
          }}
        />
      )}

      {showJsonForm && (
        <JsonBulkUploadForm
          onClose={() => setShowJsonForm(false)}
          onSuccess={() => {
            setShowJsonForm(false);
            showSuccessMessage("Product created from JSON! It may take a moment to appear.");
          }}
        />
      )}

      {successMessage && (
        <div className="success-message">{successMessage}</div>
      )}

      {showManageProduct && selectedProduct && (
        <UpdateProduct
          product={selectedProduct}
          onClose={() => {
            setShowManageProduct(false);
            setSelectedProduct(null);
          }}
          onProductUpdated={() => {
            setShowManageProduct(false);
            setSelectedProduct(null);
            showSuccessMessage("Product updated successfully!");
          }}
        />
      )}

      {showOfflineOrderModal && (
        <OfflineOrderModal
          selectedProducts={filteredActiveProducts.filter(p => selectedProductIds.has(p.productId))}
          onClose={() => setShowOfflineOrderModal(false)}
          onSuccess={(result) => {
            setShowOfflineOrderModal(false);
            setSelectedProductIds(new Set());
            setOfflineOrderMode(false);
            refreshProducts();
            showSuccessMessage(
              `Offline order created successfully! Order ID: ${result?.orderId || "N/A"}`
            );
          }}
        />
      )}
    </div>
  );
}

/* ---------- ENHANCED ProductCard with Bulk Selection ---------- */
function ProductCard({
  product,
  isExpanded,
  onToggleExpanded,
  onDelete,
  onInactivate,
  onManage,
  bulkMode = false,
  isSelected = false,
  onToggleSelect
}) {
  const [showInactivateConfirm, setShowInactivateConfirm] = useState(false);
  const [inactivateComment, setInactivateComment] = useState("");

  const handleInactivate = () => {
    const comment = inactivateComment.trim() || "Temporarily inactive";
    onInactivate(comment);
    setShowInactivateConfirm(false);
    setInactivateComment("");
  };

  const mainImage = getMainImage(product);

  const totalStock =
    product.variants?.reduce(
      (sum, v) => sum + (v.stockQuantity || 0),
      0
    ) || 0;

  const validPrices =
    product.variants
      ?.map((v) => v.offerPrice || v.originalPrice)
      .filter((price) => price && !isNaN(price)) || [];

  const minPrice = validPrices.length > 0 ? Math.min(...validPrices) : 0;
  const maxPrice = validPrices.length > 0 ? Math.max(...validPrices) : 0;

  const formatPrice = (price) =>
    price ? `₹${Number(price).toFixed(2)}` : "N/A";
  const priceDisplay =
    minPrice === maxPrice
      ? formatPrice(minPrice)
      : `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`;

  return (
    <div className={`product-card ${bulkMode ? 'bulk-mode' : ''} ${isSelected ? 'selected' : ''}`}>
      {/* 🔹 NEW: Bulk selection checkbox */}
      {bulkMode && (
        <div className="bulk-checkbox-container">
          <input
            type="checkbox"
            className="bulk-checkbox"
            checked={isSelected}
            onChange={onToggleSelect}
            aria-label={`Select ${product.productName}`}
          />
        </div>
      )}

      <div className="product-image-container">
        <img
          src={mainImage}
          alt={product.productName}
          className="product-image"
          loading="lazy"
        />
        <div className="product-status">
          <span className="status-badge active">Active</span>
        </div>
      </div>

      <div className="product-info">
        <h3 className="product-title">
          {product.productName || "Untitled Product"}
        </h3>
        <p className="product-description">
          {product.productDescription || "No description available"}
        </p>

        <div className="product-meta">
          <div className="meta-item">
            <span className="meta-label">Category:</span>
            <span className="meta-value">
              {product.category?.name || product.categoryName || "N/A"}
            </span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Sub-Category:</span>
            <span className="meta-value">
              {product.subCategory?.subCategoryName ||
                product.subCategoryName ||
                "N/A"}
            </span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Seller:</span>
            <span className="meta-value">
              {product.seller?.sellerName || product.sellerName || "N/A"}
            </span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Created:</span>
            <span className="meta-value">
              {product.createdAt
                ? new Date(product.createdAt).toLocaleDateString('en-IN', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric'
                })
                : "N/A"
              }
            </span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Colors:</span>
            <span className="meta-value">
              {(() => {
                const variantColors = product.variants
                  ?.map(v => v.color)
                  .filter(Boolean)
                  .filter((color, index, arr) => arr.indexOf(color) === index); // unique colors
                return variantColors?.length > 0 ? variantColors.join(', ') : 'N/A';
              })()}
            </span>
          </div>
        </div>

        <div className="product-stats">
          <div className="stat-item">
            <span className="stat-value">{priceDisplay}</span>
            <span className="stat-label">Price Range</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{totalStock}</span>
            <span className="stat-label">Total Stock</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{product.variants?.length || 0}</span>
            <span className="stat-label">Variants</span>
          </div>
        </div>

        {isExpanded && product.variants?.length > 0 && (
          <div className="variants-section">
            <h4>Variants &amp; Pricing</h4>
            <div className="variants-grid">
              {product.variants.map((variant) => (
                <div key={variant.variantId || variant.id} className="variant-card">
                  <div className="variant-size">
                    {variant.size?.code ||
                      variant.sizeName ||
                      variant.size?.name ||
                      "N/A"}
                  </div>
                  <div className="variant-sku">{variant.sku}</div>
                  <div className="variant-prices">
                    <span className="original-price">
                      ₹{Number(variant.originalPrice || 0).toFixed(2)}
                    </span>
                    <span className="offer-price">
                      ₹{Number(variant.offerPrice || variant.originalPrice || 0).toFixed(2)}
                    </span>
                  </div>
                  <div
                    className={`variant-stock ${variant.stockQuantity < 10 ? "low-stock" : ""
                      }`}
                  >
                    Stock: {variant.stockQuantity}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {isExpanded && product.images?.length > 0 && (
          <div className="images-section">
            <h4>Product Images</h4>
            <div className="product-images-grid">
              {product.images.map((img, idx) => (
                <div key={idx} className="image-item">
                  <img
                    src={img.image_url || img.imageUrl}
                    alt={`Product ${idx}`}
                    loading="lazy"
                  />
                  <div className="image-info">
                    <small className="image-type">
                      {img.image_type || img.imageType || "Image"}
                    </small>
                    {img.color && (
                      <small className="image-color" style={{ display: 'block', marginTop: '2px', fontWeight: 'bold' }}>
                        Color: {img.color}
                      </small>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="product-actions">
          <button className="btn-expand" onClick={onToggleExpanded}>
            {isExpanded ? "▲ Collapse" : "▼ Expand Details"}
          </button>
          <button className="btn-inactivate" onClick={() => setShowInactivateConfirm(true)}>
            📴 Inactivate
          </button>
          <button className="btn-delete" onClick={onDelete}>
            🗑️ Delete
          </button>
          <button className="btn-manage" onClick={onManage}>
            ⚙️ Update Product
          </button>
        </div>

        {/* Inactivate Confirmation Dialog */}
        {showInactivateConfirm && (
          <div className="inactivate-confirm-overlay">
            <div className="inactivate-confirm-dialog">
              <h4>Inactivate Product</h4>
              <p>Are you sure you want to inactivate this product?</p>
              <div className="form-group">
                <label>Reason (optional):</label>
                <textarea
                  value={inactivateComment}
                  onChange={(e) => setInactivateComment(e.target.value)}
                  placeholder="e.g., Sold offline, Out of stock, etc."
                  rows="3"
                />
              </div>
              <div className="dialog-actions">
                <button
                  className="btn-cancel"
                  onClick={() => {
                    setShowInactivateConfirm(false);
                    setInactivateComment("");
                  }}
                >
                  Cancel
                </button>
                <button className="btn-confirm-inactivate" onClick={handleInactivate}>
                  Inactivate
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- JSON Bulk Upload Form ---------- */
function JsonBulkUploadForm({ onClose, onSuccess }) {
  const [jsonInput, setJsonInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const sampleJson = {
    productName: "Boys Classic Polo Shirt",
    color: "Navy Blue",
    productDescription:
      "Soft cotton polo with ribbed collar — everyday comfort.",
    categoryName: "Boys",
    subCategoryName: "Shirts",
    variants: [
      {
        sku: "KW-POLO-NV-S",
        sizeName: "S",
        originalPrice: 599.0,
        discount: 10,
        offerPrice: 539.1,
        stockQuantity: 50,
        vendorId: 101,
      },
    ],
  };

  const handleSubmit = async () => {
    if (!jsonInput.trim()) {
      setError("Please enter JSON data");
      return;
    }

    try {
      const productData = JSON.parse(jsonInput);
      setLoading(true);
      setError("");

      const productResponse = await fetch(`${config.baseUrl}/api/products`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          productName: productData.productName,
          color: productData.color,
          productDescription: productData.productDescription,
          categoryName: productData.categoryName,
          subCategoryName: productData.subCategoryName,
        }),
      });

      if (!productResponse.ok) throw new Error("Failed to create product");
      const product = await productResponse.json();
      const productId = product.productId || product.id;

      if (productData.variants?.length) {
        const variantResponse = await fetch(
          `${config.baseUrl}/api/products/${productId}/variants`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(productData.variants),
          }
        );

        if (!variantResponse.ok)
          throw new Error("Failed to create variants");
      }

      alert("Product created successfully from JSON!");
      onSuccess && onSuccess();
      onClose && onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Create Product from JSON</h2>
          <button className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label>Product JSON Data</label>
            <textarea
              rows="20"
              className="json-textarea"
              value={jsonInput}
              onChange={(e) => setJsonInput(e.target.value)}
              placeholder={JSON.stringify(sampleJson, null, 2)}
            />
          </div>

          <div className="form-actions">
            <button type="button" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "Creating..." : "Create Product"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Inactive Product Card (PENDING REVIEW) ---------- */
function InactiveProductCard({
  product,
  onApprove,
  onReject,
  isExpanded,
  onToggleExpanded,
}) {
  const mainImage = getMainImage(product);

  const totalStock =
    product.variants?.reduce(
      (sum, v) => sum + (v.stockQuantity || 0),
      0
    ) || 0;

  const validPrices =
    product.variants
      ?.map((v) => v.offerPrice || v.originalPrice)
      .filter((price) => price && !isNaN(price)) || [];

  const minPrice = validPrices.length > 0 ? Math.min(...validPrices) : 0;
  const maxPrice = validPrices.length > 0 ? Math.max(...validPrices) : 0;

  const formatPrice = (price) =>
    price ? `₹${Number(price).toFixed(2)}` : "N/A";
  const priceDisplay =
    minPrice === maxPrice
      ? formatPrice(minPrice)
      : `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`;

  const [comment, setComment] = useState("Approved");

  return (
    <div className="product-card">
      <div className="product-image-container">
        <img
          src={mainImage}
          alt={product.productName}
          className="product-image"
          loading="lazy"
        />
        <div className="product-status">
          <span className="status-badge pending">Pending Review</span>
        </div>
      </div>

      <div className="product-info">
        <h3 className="product-title">
          {product.productName || "Untitled Product"}
        </h3>
        <p className="product-description">
          {product.productDescription || "No description available"}
        </p>

        <div className="product-meta">
          <div className="meta-item">
            <span className="meta-label">Category:</span>
            <span className="meta-value">
              {product.category?.name || product.categoryName || "N/A"}
            </span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Sub-Category:</span>
            <span className="meta-value">
              {product.subCategory?.subCategoryName ||
                product.subCategoryName ||
                "N/A"}
            </span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Seller:</span>
            <span className="meta-value">
              {product.seller?.sellerName || product.sellerName || "N/A"}
            </span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Created:</span>
            <span className="meta-value">
              {product.createdAt
                ? new Date(product.createdAt).toLocaleDateString('en-IN', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric'
                })
                : "N/A"
              }
            </span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Colors:</span>
            <span className="meta-value">
              {(() => {
                const variantColors = product.variants
                  ?.map(v => v.color)
                  .filter(Boolean)
                  .filter((color, index, arr) => arr.indexOf(color) === index); // unique colors
                return variantColors?.length > 0 ? variantColors.join(', ') : 'N/A';
              })()}
            </span>
          </div>
        </div>

        <div className="product-stats">
          <div className="stat-item">
            <span className="stat-value">{priceDisplay}</span>
            <span className="stat-label">Price Range</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{totalStock}</span>
            <span className="stat-label">Total Stock</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{product.variants?.length || 0}</span>
            <span className="stat-label">Variants</span>
          </div>
        </div>

        {isExpanded && product.variants?.length > 0 && (
          <div className="variants-section">
            <h4>Variants &amp; Pricing</h4>
            <div className="variants-grid">
              {product.variants.map((variant) => (
                <div key={variant.variantId || variant.id} className="variant-card">
                  <div className="variant-size">
                    {variant.size?.code ||
                      variant.sizeName ||
                      variant.size?.name ||
                      "N/A"}
                  </div>
                  <div className="variant-sku">{variant.sku}</div>
                  <div className="variant-prices">
                    <span className="original-price">
                      ₹{Number(variant.originalPrice || 0).toFixed(2)}
                    </span>
                    <span className="offer-price">
                      ₹{Number(variant.offerPrice || variant.originalPrice || 0).toFixed(2)}
                    </span>
                  </div>
                  <div
                    className={`variant-stock ${variant.stockQuantity < 10 ? "low-stock" : ""
                      }`}
                  >
                    Stock: {variant.stockQuantity}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {isExpanded && product.images && product.images.length > 0 && (
          <div className="images-section">
            <h4>Product Images ({product.images.length})</h4>
            <div className="product-images-grid">
              {product.images.map((img, idx) => (
                <div key={idx} className="image-item">
                  <img
                    src={img.image_url || img.imageUrl}
                    alt={`Product ${idx}`}
                    loading="lazy"
                  />
                  <div className="image-info">
                    <small className="image-type">
                      {img.image_type || img.imageType || "Image"}
                    </small>
                    {img.color && (
                      <small className="image-color" style={{ display: 'block', marginTop: '2px', fontWeight: 'bold' }}>
                        Color: {img.color}
                      </small>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Comment box */}
        <div className="review-comment">
          <label className="review-comment-label">Admin Comment</label>
          <textarea
            className="review-comment-textarea"
            placeholder="Eg. Images not clear, please re-upload high-resolution photos."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </div>

        {/* Missing Images Warning */}
        {(() => {
          const variantColors = product.variants
            ?.map(v => v.color)
            .filter(Boolean)
            .filter((color, index, arr) => arr.indexOf(color) === index) || [];

          const imageColors = product.images
            ?.map(img => img.color)
            .filter(Boolean)
            .filter((color, index, arr) => arr.indexOf(color) === index) || [];

          const missingColors = variantColors.filter(color => !imageColors.includes(color));

          if (missingColors.length > 0) {
            return (
              <div className="missing-images-warning" style={{
                background: '#fef3cd',
                border: '1px solid #ffc107',
                borderRadius: '6px',
                padding: '12px',
                margin: '12px 0',
                fontSize: '0.9rem'
              }}>
                <div style={{ fontWeight: 'bold', color: '#856404', marginBottom: '4px' }}>
                  ⚠️ Missing images for: {missingColors.join(', ')}
                </div>
                <small style={{ color: '#6c757d', display: 'block' }}>
                  Consider asking seller to upload images for all variant colors before approval.
                </small>
              </div>
            );
          }
          return null;
        })()}

        <div className="product-actions">
          <button className="btn-expand" onClick={onToggleExpanded}>
            {isExpanded ? "▲ Collapse" : "▼ Review Details"}
          </button>
          <button className="btn-approve" onClick={() => onApprove(comment)}>
            ✓ Approve &amp; Activate
          </button>
          <button className="btn-reject" onClick={() => onReject(comment)}>
            ✕ Reject
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Rejected Product Card (READ-ONLY) ---------- */
function RejectedProductCard({
  product,
  onActivate,
  isExpanded,
  onToggleExpanded,
}) {
  const [showActivateConfirm, setShowActivateConfirm] = useState(false);
  const [activateComment, setActivateComment] = useState("");

  const handleActivate = () => {
    const comment = activateComment.trim() || "Reactivated after review";
    onActivate(comment);
    setShowActivateConfirm(false);
    setActivateComment("");
  };

  const mainImage = getMainImage(product);

  const totalStock =
    product.variants?.reduce(
      (sum, v) => sum + (v.stockQuantity || 0),
      0
    ) || 0;

  const validPrices =
    product.variants
      ?.map((v) => v.offerPrice || v.originalPrice)
      .filter((price) => price && !isNaN(price)) || [];

  const minPrice = validPrices.length > 0 ? Math.min(...validPrices) : 0;
  const maxPrice = validPrices.length > 0 ? Math.max(...validPrices) : 0;

  const formatPrice = (price) =>
    price ? `₹${Number(price).toFixed(2)}` : "N/A";
  const priceDisplay =
    minPrice === maxPrice
      ? formatPrice(minPrice)
      : `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`;

  return (
    <div className="product-card">
      <div className="product-image-container">
        <img
          src={mainImage}
          alt={product.productName}
          className="product-image"
          loading="lazy"
        />
        <div className="product-status">
          <span className="status-badge inactive">Rejected</span>
        </div>
      </div>

      <div className="product-info">
        <h3 className="product-title">
          {product.productName || "Untitled Product"}
        </h3>
        <p className="product-description">
          {product.productDescription || "No description available"}
        </p>

        <div className="product-meta">
          <div className="meta-item">
            <span className="meta-label">Category:</span>
            <span className="meta-value">
              {product.category?.name || product.categoryName || "N/A"}
            </span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Sub-Category:</span>
            <span className="meta-value">
              {product.subCategory?.subCategoryName ||
                product.subCategoryName ||
                "N/A"}
            </span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Seller:</span>
            <span className="meta-value">
              {product.seller?.sellerName || product.sellerName || "N/A"}
            </span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Created:</span>
            <span className="meta-value">
              {product.createdAt
                ? new Date(product.createdAt).toLocaleDateString('en-IN', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric'
                })
                : "N/A"
              }
            </span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Colors:</span>
            <span className="meta-value">
              {(() => {
                const variantColors = product.variants
                  ?.map(v => v.color)
                  .filter(Boolean)
                  .filter((color, index, arr) => arr.indexOf(color) === index); // unique colors
                return variantColors?.length > 0 ? variantColors.join(', ') : 'N/A';
              })()}
            </span>
          </div>
        </div>

        <div className="product-stats">
          <div className="stat-item">
            <span className="stat-value">{priceDisplay}</span>
            <span className="stat-label">Price Range</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{totalStock}</span>
            <span className="stat-label">Total Stock</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">
              {product.variants?.length || 0}
            </span>
            <span className="stat-label">Variants</span>
          </div>
        </div>

        {isExpanded && product.images && product.images.length > 0 && (
          <div className="images-section">
            <h4>Product Images ({product.images.length})</h4>
            <div className="product-images-grid">
              {product.images.map((img, idx) => (
                <div key={idx} className="image-item">
                  <img
                    src={img.image_url || img.imageUrl}
                    alt={`Product ${idx}`}
                    loading="lazy"
                  />
                  <div className="image-info">
                    <small className="image-type">
                      {img.image_type || img.imageType || "Image"}
                    </small>
                    {img.color && (
                      <small className="image-color" style={{ display: 'block', marginTop: '2px', fontWeight: 'bold' }}>
                        Color: {img.color}
                      </small>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="review-comment">
          <label className="review-comment-label">Admin Comment</label>
          <div className="review-comment-readonly">
            {product.adminComment || "-"}
          </div>
        </div>

        <div className="product-actions">
          <button className="btn-expand" onClick={onToggleExpanded}>
            {isExpanded ? "▲ Collapse" : "▼ View Details"}
          </button>
          <button className="btn-approve" onClick={() => setShowActivateConfirm(true)}>
            ✓ Activate Product
          </button>
        </div>

        {/* Activate Confirmation Dialog */}
        {showActivateConfirm && (
          <div className="inactivate-confirm-overlay">
            <div className="inactivate-confirm-dialog">
              <h4>Activate Product</h4>
              <p>Are you sure you want to activate this product?</p>
              <div className="form-group">
                <label>Reason (optional):</label>
                <textarea
                  value={activateComment}
                  onChange={(e) => setActivateComment(e.target.value)}
                  placeholder="e.g., Issues resolved, Ready for sale, etc."
                  rows="3"
                />
              </div>
              <div className="dialog-actions">
                <button
                  className="btn-cancel"
                  onClick={() => {
                    setShowActivateConfirm(false);
                    setActivateComment("");
                  }}
                >
                  Cancel
                </button>
                <button className="btn-confirm-inactivate" onClick={handleActivate}>
                  Activate
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- Temporarily Inactive Product Card ---------- */
function TemporarilyInactiveProductCard({
  product,
  onActivate,
  isExpanded,
  onToggleExpanded,
}) {
  const [showActivateConfirm, setShowActivateConfirm] = useState(false);
  const [activateComment, setActivateComment] = useState("");

  const handleActivate = () => {
    const comment = activateComment.trim() || "Reactivated";
    onActivate(comment);
    setShowActivateConfirm(false);
    setActivateComment("");
  };

  const mainImage = getMainImage(product);

  const totalStock =
    product.variants?.reduce(
      (sum, v) => sum + (v.stockQuantity || 0),
      0
    ) || 0;

  const validPrices =
    product.variants
      ?.map((v) => v.offerPrice || v.originalPrice)
      .filter((price) => price && !isNaN(price)) || [];

  const minPrice = validPrices.length > 0 ? Math.min(...validPrices) : 0;
  const maxPrice = validPrices.length > 0 ? Math.max(...validPrices) : 0;

  const formatPrice = (price) =>
    price ? `₹${Number(price).toFixed(2)}` : "N/A";
  const priceDisplay =
    minPrice === maxPrice
      ? formatPrice(minPrice)
      : `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`;

  return (
    <div className="product-card">
      <div className="product-image-container">
        <img
          src={mainImage}
          alt={product.productName}
          className="product-image"
          loading="lazy"
        />
        <div className="product-status">
          <span className="status-badge inactive">Temporarily Inactive</span>
        </div>
      </div>

      <div className="product-info">
        <h3 className="product-title">
          {product.productName || "Untitled Product"}
        </h3>
        <p className="product-description">
          {product.productDescription || "No description available"}
        </p>

        <div className="product-meta">
          <div className="meta-item">
            <span className="meta-label">Category:</span>
            <span className="meta-value">
              {product.category?.name || product.categoryName || "N/A"}
            </span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Sub-Category:</span>
            <span className="meta-value">
              {product.subCategory?.subCategoryName ||
                product.subCategoryName ||
                "N/A"}
            </span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Seller:</span>
            <span className="meta-value">
              {product.seller?.sellerName || product.sellerName || "N/A"}
            </span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Created:</span>
            <span className="meta-value">
              {product.createdAt
                ? new Date(product.createdAt).toLocaleDateString('en-IN', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric'
                })
                : "N/A"
              }
            </span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Colors:</span>
            <span className="meta-value">
              {(() => {
                const variantColors = product.variants
                  ?.map(v => v.color)
                  .filter(Boolean)
                  .filter((color, index, arr) => arr.indexOf(color) === index);
                return variantColors?.length > 0 ? variantColors.join(', ') : 'N/A';
              })()}
            </span>
          </div>
        </div>

        <div className="product-stats">
          <div className="stat-item">
            <span className="stat-value">{priceDisplay}</span>
            <span className="stat-label">Price Range</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{totalStock}</span>
            <span className="stat-label">Total Stock</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">
              {product.variants?.length || 0}
            </span>
            <span className="stat-label">Variants</span>
          </div>
        </div>

        {isExpanded && product.images && product.images.length > 0 && (
          <div className="images-section">
            <h4>Product Images ({product.images.length})</h4>
            <div className="product-images-grid">
              {product.images.map((img, idx) => (
                <div key={idx} className="image-item">
                  <img
                    src={img.image_url || img.imageUrl}
                    alt={`Product ${idx}`}
                    loading="lazy"
                  />
                  <div className="image-info">
                    <small className="image-type">
                      {img.image_type || img.imageType || "Image"}
                    </small>
                    {img.color && (
                      <small className="image-color" style={{ display: 'block', marginTop: '2px', fontWeight: 'bold' }}>
                        Color: {img.color}
                      </small>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="review-comment">
          <label className="review-comment-label">Inactivation Reason</label>
          <div className="review-comment-readonly">
            {product.adminComment || "Temporarily inactivated"}
          </div>
        </div>

        <div className="product-actions">
          <button className="btn-expand" onClick={onToggleExpanded}>
            {isExpanded ? "▲ Collapse" : "▼ View Details"}
          </button>
          <button className="btn-approve" onClick={() => setShowActivateConfirm(true)}>
            ✓ Reactivate Product
          </button>
        </div>

        {/* Activate Confirmation Dialog */}
        {showActivateConfirm && (
          <div className="inactivate-confirm-overlay">
            <div className="inactivate-confirm-dialog">
              <h4>Reactivate Product</h4>
              <p>Are you sure you want to reactivate this product?</p>
              <div className="form-group">
                <label>Reason (optional):</label>
                <textarea
                  value={activateComment}
                  onChange={(e) => setActivateComment(e.target.value)}
                  placeholder="e.g., Back in stock, Ready for online sale, etc."
                  rows="3"
                />
              </div>
              <div className="dialog-actions">
                <button
                  className="btn-cancel"
                  onClick={() => {
                    setShowActivateConfirm(false);
                    setActivateComment("");
                  }}
                >
                  Cancel
                </button>
                <button className="btn-confirm-inactivate" onClick={handleActivate}>
                  Reactivate
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
